import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { UserPlus, Pencil, ShieldCheck, User, Power, FileText, KeyRound, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", role: "engineer" };

export default function Team() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [demoConfirm, setDemoConfirm] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);

  const doRemoveDemo = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/remove-demo-data");
      const r = data.removed || {};
      toast.success(
        `Demo data removed — ${r.customers || 0} customers, ${r.reports || 0} reports, ${r.engineers || 0} engineers`
      );
      setDemoConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove demo data");
    } finally {
      setDemoBusy(false);
    }
  };

  const genTempPassword = () => `Sterio${Math.floor(1000 + Math.random() * 9000)}!`;

  const openReset = (u) => {
    setResetUser(u);
    setResetPw(genTempPassword());
    setResetDone(false);
  };

  const doReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.put(`/users/${resetUser.id}`, { password: resetPw });
      setResetDone(true);
      toast.success("Password reset — share it with the engineer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reset");
    } finally {
      setSaving(false);
    }
  };

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (!editing && !form.password) return toast.error("Set a temporary password");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/users", form);
        toast.success("Engineer added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Account deactivated" : "Account reactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  const doRemove = async () => {
    const u = confirm;
    setConfirm(null);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Engineer permanently removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    }
  };

  const doPurgeInactive = async () => {
    setPurgeBusy(true);
    try {
      const { data } = await api.post("/admin/purge-inactive-users");
      toast.success(`Removed ${data.removed || 0} inactive engineer${data.removed === 1 ? "" : "s"}`);
      setPurgeConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove inactive engineers");
    } finally {
      setPurgeBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500">Manage engineers and admins.</p>
        </div>
        <Button className="h-11" onClick={openNew} data-testid="add-user-btn">
          <UserPlus className="mr-1.5 h-4 w-4" /> Add
        </Button>
      </div>

      <div className="space-y-2.5">
        {users.map((u) => (
          <Card key={u.id} className="p-4" data-testid={`user-row-${u.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${u.role === "admin" ? "bg-secondary text-white" : "bg-primary/10 text-primary"}`}>
                  {u.role === "admin" ? <ShieldCheck className="h-5 w-5" /> : <User className="h-5 w-5" />}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-900 truncate">{u.name}</span>
                    <Badge variant="outline" className="text-[10px] capitalize">{u.role}</Badge>
                    {!u.active && <Badge className="bg-red-100 text-red-700 text-[10px]" variant="secondary">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openReset(u)}
                  data-testid={`reset-pw-${u.id}`}
                  title="Reset password"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-slate-500 hover:bg-slate-50"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <span className="flex items-center gap-1.5 text-xs text-slate-400">
                  <FileText className="h-3.5 w-3.5" /> {u.report_count}
                </span>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>
                <Power className="mr-1 h-3.5 w-3.5" /> {u.active ? "Deactivate" : "Reactivate"}
              </Button>
              {u.role !== "admin" && (
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => setConfirm(u)} data-testid={`remove-user-${u.id}`}>
                  Remove
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* add / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit team member" : "Add engineer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "New password (leave blank to keep)" : "Temporary password"}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "••••••" : "Share this with the engineer"} className="h-11" data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="engineer">Engineer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button className="h-11 w-full" onClick={save} disabled={saving} data-testid="save-user-btn">
              {editing ? "Save changes" : "Add engineer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Set a temporary password and share it with the engineer. They can change it themselves after logging in.
            </p>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <div className="flex gap-2">
                <Input value={resetPw} onChange={(e) => { setResetPw(e.target.value); setResetDone(false); }} className="h-11" data-testid="reset-password-input" />
                <Button type="button" variant="outline" className="h-11 shrink-0" onClick={() => { setResetPw(genTempPassword()); setResetDone(false); }} data-testid="regenerate-pw-btn">
                  Generate
                </Button>
              </div>
            </div>
            {resetDone && (
              <div className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-2.5" data-testid="reset-done">
                <span className="text-sm font-semibold text-primary">Password set: {resetPw}</span>
                <Button type="button" size="sm" variant="ghost" className="h-8 text-primary" onClick={() => { navigator.clipboard.writeText(resetPw); toast.success("Copied"); }} data-testid="copy-reset-pw">
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            {resetDone ? (
              <Button className="h-11 w-full" onClick={() => setResetUser(null)} data-testid="reset-close-btn">Done</Button>
            ) : (
              <Button className="h-11 w-full" onClick={doReset} disabled={saving} data-testid="confirm-reset-btn">Reset password</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes their account and login. Past service reports
              are kept for the record (they show the engineer's name). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRemove} data-testid="confirm-remove-user">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* danger zone — remove inactive engineers */}
      {users.filter((u) => !u.active && u.role !== "admin").length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40 p-4" data-testid="inactive-zone">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
              <Power className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-slate-900">
                Remove inactive engineers ({users.filter((u) => !u.active && u.role !== "admin").length})
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Permanently deletes every deactivated engineer account (including old
                test logins). Report history keeps each engineer's name.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setPurgeConfirm(true)}
                data-testid="purge-inactive-btn"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove inactive engineers
              </Button>
            </div>
          </div>
        </Card>
      )}

      <AlertDialog open={purgeConfirm} onOpenChange={setPurgeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all inactive engineers?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every deactivated engineer account. Active
              accounts and admins are not affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doPurgeInactive}
              disabled={purgeBusy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-purge-inactive"
            >
              {purgeBusy ? "Removing…" : "Remove all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* danger zone — clear seeded demo data */}
      <Card className="border-red-200 bg-red-50/40 p-4" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <Trash2 className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-slate-900">Remove demo data</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Deletes the sample customers (Murphy Residence, Glenview Care Home,
              Walsh Engineering Ltd), their reports, and the demo engineer logins.
              Your imported clients are not affected.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-red-300 text-red-600 hover:bg-red-100"
              onClick={() => setDemoConfirm(true)}
              data-testid="remove-demo-data-btn"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove demo data
            </Button>
          </div>
        </div>
      </Card>

      <AlertDialog open={demoConfirm} onOpenChange={setDemoConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the seeded sample customers, their sites, heat
              pumps, reports and jobs, plus the demo engineer accounts. Your imported
              real clients stay untouched. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRemoveDemo}
              disabled={demoBusy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-demo"
            >
              {demoBusy ? "Removing…" : "Remove demo data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
